import { LocalStorageDocument } from '@app/core/storage/browser/LocalStorageDocument';
import { Migration } from '@app/core/versionedSerializer/versionedSerializer';

import {
  FavoritePayment,
  readPayment,
  writePayment,
} from './FavoritePayment';

const CURRENT_VERSION = 1;
const TRANSFERS_SETTINGS_NODE = 'transfers-settings';

export interface ITransfersLocalSettings {
  serializedFavoritePayments:string[];
}

/**
 * Singletone class for managing transfer & payments settings stored in local storage.
 */
export class TransfersLocalSettings extends LocalStorageDocument<ITransfersLocalSettings> {
  /**
   * Singleton methods
   */

  private static inner?:TransfersLocalSettings;

  /**
   * Constructor must describe latest data version and provide migrations from previous versions of saved data.
   * @param node string node key to storage data in local storage.
   * @param version latest version.
   * @param migrations migrations from previous versions to latest.
   */
  private constructor(node:string, version:number, migrations:Migration[] = []) {
    super(node, version, migrations);

    if (this.favoritePayments == null) {
      this.favoritePayments = [];
    }
  }

  public static get instance():TransfersLocalSettings {
    if (!this.inner) {
      this.inner = new TransfersLocalSettings(TRANSFERS_SETTINGS_NODE, CURRENT_VERSION);
    }

    return this.inner;
  }

  /**
   * Public interface methods
   */

  private favoritePayments!:FavoritePayment[];

  public getFavoritePayments(): FavoritePayment[] {
    return [...this.favoritePayments];
  }

  public saveFavoritePayments(value:FavoritePayment[]) {
    this.favoritePayments = value;
    this.save();
  }

  /**
   * Abstract methods implementation
   */

  /**
   * Save public data properties into serializable data stracture.
   * @returns data structure which can be saved in local storage.
   */
  protected serialize():ITransfersLocalSettings {
    return {
      // Serialize each favorite payment into string.
      serializedFavoritePayments: this.favoritePayments.map((f) => writePayment(f)),
    };
  }

  /**
   * Init public data properties with saved data.
   * @param deserialized saved data.
   */
  protected deserialize(deserialized:ITransfersLocalSettings) {
    // Restore saved favorite payment strings into class instances.
    this.favoritePayments = deserialized.serializedFavoritePayments.map((saved) => readPayment(saved));
  }
}
